import { RequiredParamException } from "api/exceptions/InvalidParamException";
import {
  addDoc,
  deleteDoc,
  getDocs,
  query,
  Transaction,
  Unsubscribe,
  where,
} from "firebase/firestore";
import { ICustomPropsDefinitions } from "../interfaces";
import ContractModel from "../models/ContractModel";
import {
  callFunction,
  fromDocumentData,
  getCollectionRef,
  getDocumentRef,
  onCollectionSnapshot,
  onDocumentSnapshot,
  setDocument,
  toDocumentData,
  updateDocument,
} from "./FirebaseRepository";

class ContractRepository {
  private path = "cfContract";
  private pathStatusLog = (contractUid: string) =>
    `cfContract/${contractUid}/statusChangedLog`;
  private pathCustomProps = (contractUid: string) =>
    `cfContract/${contractUid}/customProperties`;

  public async addContractToDataBase(
    uid: string,
    data: ContractModel
  ): Promise<void> {
    const doc = getDocumentRef(this.path, uid, converter);
    return setDocument(doc, data);
  }

  public updateContractOnDataBase(
    uid: string,
    updatedData: Partial<ContractModel>,
    transaction?: Transaction
  ) {
    if (!uid) throw new RequiredParamException("uid");

    const doc = getDocumentRef(this.path, uid);
    const data = converter.toFirestore(updatedData);
    return updateDocument(doc, data, transaction);
  }

  async deleteContractFromDataBase(uid: string): Promise<void> {
    if (!uid) throw new RequiredParamException("uid");

    this.deleteContractsWBS(uid).then((deleted) => {
      if (deleted) return this.deleteContractsSubCollections(uid);
      return;
    });

    const doc = getDocumentRef(this.path, uid);
    return deleteDoc(doc);
  }

  private async deleteContractsSubCollections(uid: string): Promise<boolean> {
    const data = { IDContract: uid };
    return callFunction("deleteData-contract", data)
      .then((response) => {
        const data: any = response.data;
        if (data?.status !== "OK") {
          console.error(data);
          return false;
        }
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
  }

  private async deleteContractsWBS(uid: string): Promise<boolean> {
    const data = { IDContract: uid };
    return callFunction("deleteData-wbsContract", data)
      .then((response) => {
        const data: any = response.data;
        if (data?.status !== "OK") {
          console.error(data);
          return false;
        }
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
  }

  async addStatusChangedLog(uid: string, data: any): Promise<boolean> {
    if (!uid) throw new RequiredParamException("uid");

    const collection = getCollectionRef(this.pathStatusLog(uid));
    data.modifiedAt = new Date();

    const doc = await addDoc(collection, toDocumentData(data));
    return Boolean(doc);
  }

  public getCustomProperties(uid: string): Promise<ICustomPropsDefinitions[]> {
    if (!uid) throw new RequiredParamException("uid");

    const collection = getCollectionRef(this.pathCustomProps(uid));
    return getDocs(collection).then((snapshot) =>
      snapshot.docs.map((doc) => doc.data() as ICustomPropsDefinitions)
    );
  }

  public addContractDocumentSnapshot(
    uid: string,
    onCreate: (data: ContractModel) => void,
    onChange: (data: ContractModel) => void,
    onError?: Function
  ): Unsubscribe {
    if (!uid) throw new RequiredParamException("uid");

    const doc = getDocumentRef(this.path, uid, converter);
    return onDocumentSnapshot<ContractModel>(
      doc,
      onCreate,
      onChange,
      (error) => {
        console.error("ContractSnapshot", error);
        if (onError) onError(error);
      }
    );
  }

  public addContractCollectionSnapshotByUser(
    userId: string,
    onCreate: (data: ContractModel[]) => void,
    onChange: (data: ContractModel[]) => void,
    onRemove: (data: ContractModel[]) => void
  ): Unsubscribe {
    if (!userId) throw new RequiredParamException("userId");

    const collection = getCollectionRef(this.path, converter);
    const queryRef = query(
      collection,
      where(
        `users.${userId}`,
        "in",
        [0, 1]
      ) /*Que seja do usuario ou compartilhado com o mesmo*/
    );

    return onCollectionSnapshot(
      queryRef,
      onCreate,
      onChange,
      onRemove,
      (error) => {
        console.error("ContractsSnapshot", error);
      }
    );
  }
}

// Firestore data converter
const converter = {
  toFirestore: (contract: any) => {
    const data =
      contract instanceof ContractModel ? contract.toSave() : contract;
    return toDocumentData(data);
  },
  fromFirestore: (snapshot, options) => {
    let data = snapshot.data(options);
    data = fromDocumentData(data);
    return ContractModel.create(snapshot.id, data);
  },
};

export default new ContractRepository();
